import { useCallback, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Server } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'

const FOCUS_MODE_KEY = 'bekat-dash-tv-mode'

const PAGE_META = {
  '/': { title: 'Dashboard', subtitle: 'Estate overview' },
  '/customers': { title: 'Customers', subtitle: 'Companies synced from Atera' },
  '/agents': { title: 'Devices', subtitle: 'Endpoints and servers' },
  '/nas': {
    title: 'NAS Backup',
    subtitle: 'Synology health, Hyper Backup and Cloud Sync for configured NAS devices',
    Icon: Server,
  },
  '/backup': { title: 'Server Backup', subtitle: 'Altaro VM backup status for configured servers' },
  '/threatdown': { title: 'ThreatDown', subtitle: 'Endpoint protection from OneView' },
  '/threatdown/detections': {
    title: 'ThreatDown detections',
    subtitle: 'Threat events across the estate',
  },
  '/defender': {
    title: 'Windows Defender',
    subtitle: 'Defender posture from RMM snapshots across the estate',
  },
  '/defender/detections': {
    title: 'Windows Defender detections',
    subtitle: 'Threat detections reported by Defender on endpoints',
  },
  '/reports': { title: 'Reports', subtitle: 'Predefined audit and compliance reports' },
  '/assistant': { title: 'Assistant', subtitle: 'Ask questions about your portal data' },
  '/admin/users': { title: 'User management', subtitle: 'Portal accounts and roles' },
  '/admin/sync': { title: 'Sync control', subtitle: 'Atera, ThreatDown, and Zabbix synchronisation' },
  '/admin/ingest': {
    title: 'RMM ingest',
    subtitle: 'Script upload status and last received times',
  },
  '/admin/settings': { title: 'Settings', subtitle: 'Portal configuration and integrations' },
}

function getPageMeta(pathname) {
  if (pathname.startsWith('/agents/') && pathname !== '/agents') {
    return { title: 'Device detail', subtitle: 'Full device information' }
  }
  if (pathname.startsWith('/customers/') && pathname !== '/customers') {
    return { title: 'Customer detail', subtitle: 'Company assets and devices' }
  }
  if (pathname.startsWith('/threatdown/endpoints/')) {
    return {
      title: 'ThreatDown endpoint',
      subtitle: 'Protection and asset detail from OneView',
    }
  }
  return PAGE_META[pathname] || { title: 'Bekat Dash', subtitle: '' }
}

function readStoredFocusMode() {
  try {
    return sessionStorage.getItem(FOCUS_MODE_KEY) === '1'
  } catch {
    return false
  }
}

export function AppLayout() {
  const { pathname } = useLocation()
  const { user, isAdmin, isStaff, canFilterCustomers } = useAuth()
  const { title, subtitle, Icon } = getPageMeta(pathname)
  const [navOpen, setNavOpen] = useState(false)
  const [tvMode, setTvMode] = useState(readStoredFocusMode)

  useEffect(() => {
    setNavOpen(false)
  }, [pathname])

  useEffect(() => {
    try {
      sessionStorage.setItem(FOCUS_MODE_KEY, tvMode ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [tvMode])

  const enterBrowserFullscreen = useCallback(async () => {
    const el = document.documentElement
    if (!document.fullscreenElement && el.requestFullscreen) {
      try {
        await el.requestFullscreen()
      } catch {
        /* user gesture / policy — sidebar hide still applies */
      }
    }
  }, [])

  const exitBrowserFullscreen = useCallback(async () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      try {
        await document.exitFullscreen()
      } catch {
        /* ignore */
      }
    }
  }, [])

  const toggleTvMode = useCallback(async () => {
    const next = !tvMode
    setTvMode(next)
    if (next) {
      setNavOpen(false)
      await enterBrowserFullscreen()
    } else {
      await exitBrowserFullscreen()
    }
  }, [tvMode, enterBrowserFullscreen, exitBrowserFullscreen])

  useEffect(() => {
    function onFullscreenChange() {
      if (!document.fullscreenElement && tvMode) {
        // Esc left browser fullscreen — keep sidebar hidden until they turn TV mode off.
      }
    }
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [tvMode])

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        showAdmin={isAdmin}
        showStaff={isStaff && !isAdmin}
        isCustomer={user?.role === 'customer'}
        canFilterCustomers={canFilterCustomers}
        mobileOpen={navOpen}
        onClose={() => setNavOpen(false)}
        desktopHidden={tvMode}
      />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar
          title={title}
          subtitle={subtitle}
          icon={Icon}
          onMenuClick={() => setNavOpen(true)}
          tvMode={tvMode}
          onToggleTvMode={toggleTvMode}
          forceMenuButton={tvMode}
        />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
